<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  ref,
  useAttrs,
  watch,
  type Component,
  type FunctionalComponent,
} from 'vue'
import AppBadge from '../AppBadge/AppBadge.vue'
import AppDrawnCheck from '../AppDrawnCheck/AppDrawnCheck.vue'
import AppSpinner from '../AppSpinner/AppSpinner.vue'
import AppShortcut from '../AppShortcut/AppShortcut.vue'
import AppTooltip from '../AppTooltip/AppTooltip.vue'
import type { KbdTone } from '../AppKbd/kbd-types'
import {
  ACCENT_KEY,
  GLASS_KEY,
  GLASS_KEY_EDGE,
  GLASS_KEY_PRESS,
  NEUTRAL_KEY,
  NEUTRAL_KEY_EDGE,
  NEUTRAL_KEY_PRESS,
  PRIMARY_KEY,
  PRIMARY_KEY_SHADOW,
} from '../control-surface'
import { vPointerLight } from '../v-pointer-light'
import {
  ariaKeyShortcuts,
  detectShortcutPlatform,
  type KeyboardShortcut,
} from '../../../lib/keyboard-shortcut'
import type { ButtonFlush, ButtonType, ButtonVariant } from './button-types'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    label?: string
    variant?: ButtonVariant
    /** Native button `type`: `button` (default) · `submit`. */
    type?: ButtonType
    /** Control size, one fixed total height for labelled and icon-only buttons: `xs` 24px · `sm` 28px · `md` 36px (default) · `lg` 50px. */
    size?: 'xs' | 'sm' | 'md' | 'lg'
    /** Corner radius on the shared scale: `none` · `sm` · `md` (default) · `full` (pill, or a round icon-only button). */
    radius?: 'none' | 'sm' | 'md' | 'full'
    /** Adds `shadow-2` to float the button, e.g. a FAB. */
    elevated?: boolean
    /** Leading icon component (e.g. a Solar icon). Also used as the glyph for `iconOnly`. */
    startIcon?: Component | null
    endIcon?: Component | null
    /** Icon-only button - renders `startIcon`, no label: a square at the `radius`; pass `radius="full"` for a circle. */
    iconOnly?: boolean
    /** Force the hover fill, for specimens without a real pointer. */
    isHovered?: boolean
    /** Force the pressed fill, for specimens without a real pointer. */
    isPressed?: boolean
    isInactive?: boolean
    /** Why the button is inactive. It then stays focusable as `aria-disabled`, ignores activation and shows this reason as its tooltip on hover and focus. */
    inactiveReason?: string
    /** Pulls a labelled `ghost` button out by its own side padding, so its label lines up with the content edge on that side. */
    flush?: ButtonFlush
    /** Work is in flight: disables the button and crossfades the content to a spinner. */
    loading?: boolean
    /** Announced while `loading`; defaults to the label. */
    loadingLabel?: string
    badge?: number
    /** The start icon turns once on each click, for a retry or a redo. */
    turnsIcon?: boolean
    /** The work landed: turns the button green, disables it and swaps the label for a drawn check and `successLabel`. */
    success?: boolean
    /** The word beside the drawn check while `success` is on. */
    successLabel?: string
    /** Keyboard shortcut shown as a key cap after the label (not on icon-only buttons) and announced through aria-keyshortcuts. */
    shortcut?: KeyboardShortcut | null
  }>(),
  {
    label: '',
    variant: 'primary',
    type: 'button',
    size: 'md',
    radius: 'md',
    elevated: false,
    startIcon: null,
    endIcon: null,
    iconOnly: false,
    isHovered: false,
    isPressed: false,
    isInactive: false,
    inactiveReason: undefined,
    flush: undefined,
    loading: false,
    loadingLabel: undefined,
    badge: undefined,
    turnsIcon: false,
    success: false,
    successLabel: '',
    shortcut: null,
  },
)

const shortcutPlatform = detectShortcutPlatform()
const ariaKeyShortcutsValue = computed(() =>
  props.shortcut ? ariaKeyShortcuts(props.shortcut, shortcutPlatform) : undefined,
)

const SPINNER_DELAY_MS = 150

const showSpinner = ref(false)
let spinnerTimer: ReturnType<typeof setTimeout> | undefined

watch(
  () => props.loading,
  (isLoading) => {
    clearTimeout(spinnerTimer)
    if (!isLoading) {
      showSpinner.value = false
      return
    }
    spinnerTimer = setTimeout(() => {
      showSpinner.value = true
    }, SPINNER_DELAY_MS)
  },
  { immediate: true },
)

onBeforeUnmount(() => clearTimeout(spinnerTimer))

const hasReason = computed(() => Boolean(props.inactiveReason) && !props.loading && !props.success)
const looksInactive = computed(() => (props.isInactive || hasReason.value) && !props.success)
const isDisabled = computed(
  () => !hasReason.value && (props.isInactive || props.loading || props.success),
)

const attrs = useAttrs()
function forwardedAttrs(): Record<string, unknown> {
  if (!hasReason.value) return attrs
  return Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'onClick'))
}

const NoTooltip: FunctionalComponent<{ content?: string }> = (_props, { slots }) => {
  const nodes = slots.trigger?.({ aria: {} }) ?? []
  return nodes.length === 1 ? nodes[0] : nodes
}
NoTooltip.props = ['content']

const wrapper = computed(() => (hasReason.value ? AppTooltip : NoTooltip))

function describedBy(aria: { 'aria-describedby'?: string }): { 'aria-describedby'?: string } {
  const ids = [attrs['aria-describedby'], aria['aria-describedby']].filter(
    (id): id is string => typeof id === 'string' && id !== '',
  )
  return ids.length ? { 'aria-describedby': ids.join(' ') } : {}
}

type VariantStyle = {
  surface: string
  shadow: string
}

const NEUTRAL_KEY_STYLE: VariantStyle = {
  surface: `${NEUTRAL_KEY} ${NEUTRAL_KEY_EDGE} ${NEUTRAL_KEY_PRESS} pointer-glow`,
  shadow: 'shadow-contact',
}

const GLASS_KEY_STYLE: VariantStyle = {
  surface: `${GLASS_KEY} ${GLASS_KEY_EDGE} ${GLASS_KEY_PRESS}`,
  shadow: 'shadow-contact',
}

const CONSTRUCTIVE_STYLE: VariantStyle = {
  surface: `${ACCENT_KEY} bg-accent-green inset-ring-accent-green-active/30 pressed:bg-accent-green-hover`,
  shadow: 'shadow-lift shadow-accent-green/30',
}

const VARIANTS: Record<ButtonVariant, VariantStyle> = {
  primary: {
    surface: PRIMARY_KEY,
    shadow: PRIMARY_KEY_SHADOW,
  },
  destructive: {
    surface: `${ACCENT_KEY} bg-accent-red inset-ring-accent-red-active/30 pressed:bg-accent-red-hover`,
    shadow: 'shadow-lift shadow-accent-red/30',
  },
  constructive: CONSTRUCTIVE_STYLE,
  secondary: NEUTRAL_KEY_STYLE,
  surface: NEUTRAL_KEY_STYLE,
  glass: GLASS_KEY_STYLE,
  'sec-destructive': {
    surface:
      'bg-accent-red/10 text-accent-red inset-ring-1 inset-ring-transparent hovered:bg-accent-red/20 hovered:inset-ring-accent-red/30 pressed:bg-accent-red/25 pressed:inset-shadow-well',
    shadow: '',
  },
  ghost: {
    surface: 'text-text-tertiary hover-fill hovered:text-text-primary pressed:text-text-primary',
    shadow: '',
  },
  'ghost-strong': {
    surface: 'text-text-primary hover-fill',
    shadow: '',
  },
  link: {
    surface: 'rounded-1 text-start text-accent-blue hover:underline',
    shadow: '',
  },
  'link-quiet': {
    surface: 'rounded-1 text-start text-inherit hover:text-text-primary hover:underline',
    shadow: '',
  },
}

const variantStyle = computed(() => (props.success ? CONSTRUCTIVE_STYLE : VARIANTS[props.variant]))

const SHORTCUT_TONE: Record<ButtonVariant, KbdTone> = {
  primary: 'on-accent',
  destructive: 'on-accent',
  constructive: 'on-accent',
  secondary: 'neutral',
  surface: 'neutral',
  glass: 'on-glass',
  'sec-destructive': 'neutral',
  ghost: 'neutral',
  'ghost-strong': 'neutral',
  link: 'neutral',
  'link-quiet': 'neutral',
}

const shortcutTone = computed(() => (props.success ? 'on-accent' : SHORTCUT_TONE[props.variant]))

const SIZE_SCALE = {
  xs: {
    text: 'text-caption-emphasized',
    box: 'h-(--control-height-xs) px-4',
    iconBox: 'size-(--control-height-xs)',
  },
  sm: {
    text: 'text-caption-emphasized',
    box: 'h-(--control-height-sm) px-4',
    iconBox: 'size-(--control-height-sm)',
  },
  md: {
    text: 'text-body-sm-emphasized',
    box: 'h-(--control-height-md) px-8',
    iconBox: 'size-(--control-height-md)',
  },
  lg: {
    text: 'text-body-emphasized',
    box: 'h-(--control-height-lg) px-8',
    iconBox: 'size-(--control-height-lg)',
  },
} as const

const RADIUS = {
  none: 'rounded-0',
  sm: 'rounded-2',
  md: 'rounded-4',
  full: 'rounded-full',
} as const

const radiusClass = computed(() => RADIUS[props.radius])

const badgeTone = computed(() =>
  props.variant === 'primary' || props.variant === 'destructive' ? 'neutral' : 'blue',
)

const isLink = computed(
  () => (props.variant === 'link' || props.variant === 'link-quiet') && !props.success,
)

const sizing = computed(() => {
  if (isLink.value) return { text: '', box: '' }
  const s = SIZE_SCALE[props.size]
  return {
    text: s.text,
    box: props.iconOnly ? s.iconBox : s.box,
  }
})

const FLUSH_MARGIN = {
  sm: { start: '-ms-4', end: '-me-4' },
  md: { start: '-ms-8', end: '-me-8' },
} as const

const flushClass = computed(() => {
  const hangs = props.variant === 'ghost' || props.variant === 'ghost-strong'
  if (!props.flush || !hangs || props.iconOnly) return ''
  if (props.size !== 'sm' && props.size !== 'md') return ''
  return FLUSH_MARGIN[props.size][props.flush]
})

const turning = ref(false)

function onClick(event: MouseEvent): void {
  if (hasReason.value) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  if (props.turnsIcon) turning.value = true
}

function onKeydown(event: KeyboardEvent): void {
  if (hasReason.value && (event.key === 'Enter' || event.key === ' ')) event.preventDefault()
}
</script>

<template>
  <component :is="wrapper" :content="inactiveReason">
    <template #trigger="{ aria }">
      <button
        v-bind="{ ...forwardedAttrs(), ...describedBy(aria) }"
        v-pointer-light
        :type="type"
        :disabled="isDisabled"
        :aria-disabled="hasReason || undefined"
        :aria-busy="(loading && !success) || undefined"
        :aria-keyshortcuts="ariaKeyShortcutsValue"
        :data-hovered="isHovered || undefined"
        :data-pressed="isPressed || undefined"
        :class="[
          isLink
            ? 'inline'
            : 'control-press relative inline-flex select-none items-center justify-center gap-4',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue',
          iconOnly ? 'press-small' : '',
          flushClass,
          isLink ? '' : radiusClass,
          variantStyle.surface,
          elevated ? 'shadow-2' : variantStyle.shadow,
          size === 'xs' || (iconOnly && (size === 'sm' || size === 'md')) ? 'icon-sm' : '',
          sizing.text,
          sizing.box,
          looksInactive ? 'opacity-40' : '',
          success
            ? 'cursor-default'
            : looksInactive
              ? 'cursor-not-allowed'
              : loading
                ? 'cursor-progress'
                : 'cursor-pointer',
        ]"
        @click="onClick"
        @keydown="onKeydown"
      >
        <span class="inline-grid items-center justify-items-center">
          <span
            class="button-face col-start-1 row-start-1 inline-flex items-center justify-center gap-4"
            :class="showSpinner && !success ? 'opacity-0' : ''"
            :data-lifted="success ? 'true' : 'false'"
            :aria-hidden="success || undefined"
          >
            <span
              v-if="$slots.icon || startIcon"
              class="inline-flex shrink-0"
              :class="turning ? 'motion-safe:animate-turn' : ''"
              @animationend="turning = false"
            >
              <slot name="icon">
                <component :is="startIcon" />
              </slot>
            </span>
            <span v-if="!iconOnly">{{ label }}</span>
            <AppBadge v-if="!iconOnly && badge && badge > 0" :tone="badgeTone">{{
              badge
            }}</AppBadge>
            <AppShortcut
              v-if="shortcut && !iconOnly"
              class="hover-none:hidden"
              aria-hidden="true"
              :shortcut="shortcut"
              :tone="shortcutTone"
            />
            <component :is="endIcon" v-if="endIcon && !iconOnly" class="button-end-icon shrink-0" />
          </span>
          <span
            v-if="successLabel"
            class="button-success col-start-1 row-start-1 inline-flex items-center justify-center gap-4"
            :data-shown="success ? 'true' : 'false'"
            :aria-hidden="!success || undefined"
          >
            <AppDrawnCheck :drawn="success" animated />
            <span>{{ successLabel }}</span>
          </span>
        </span>

        <span
          v-if="loading && !success"
          :class="[
            'absolute inset-0 inline-flex items-center justify-center',
            'transition-opacity duration-fast ease-standard motion-reduce:transition-none',
            showSpinner ? 'opacity-100' : 'opacity-0',
          ]"
        >
          <AppSpinner tone="current" :label="loadingLabel ?? label" />
        </span>
      </button>
    </template>
  </component>
</template>
